import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ArrowRight, ShieldCheck, AlertCircle } from "lucide-react";
import SEO from "@/components/SEO";
import { api, useAuth, formatApiError } from "@/auth/AuthContext";

export default function Activate() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [info, setInfo] = useState(null);
  const [invalidMsg, setInvalidMsg] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const { startSession } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) { setInvalidMsg("This invitation link is invalid."); return; }
    api.get(`/auth/invitations/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setInvalidMsg(formatApiError(e, "This invitation link is invalid.")));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setLoading(true); setError(null);
    try {
      const { data } = await api.post("/auth/activate", { token, password });
      startSession(data.token, data.user);
      navigate("/portal");
    } catch (err) {
      setError(formatApiError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F7F5F0] px-6 py-16 font-body text-[#121212]">
      <SEO title="Activate Your Account — The China Explorer" description="Activate your B2B travel account." path="/activate" />
      <div className="w-full max-w-md">
        <p className="text-center font-heading text-xl text-[#0E2A5C] mb-8">The China Explorer</p>

        {invalidMsg && (
          <div className="bg-white border border-[#EAE6DF] p-8 md:p-10 text-center" data-testid="activate-invalid">
            <AlertCircle className="mx-auto mb-4 text-[#C62828]" size={30}/>
            <h1 className="font-heading text-2xl text-[#0E2A5C]">Invitation unavailable</h1>
            <p className="mt-3 text-sm text-[#4A4A4A] leading-relaxed">{invalidMsg}</p>
            <Link to="/login" className="mt-6 inline-block text-xs uppercase tracking-[0.2em] font-semibold text-[#0E2A5C] hover:text-[#C62828]">
              Go to sign in
            </Link>
          </div>
        )}

        {!invalidMsg && !info && (
          <div className="bg-white border border-[#EAE6DF] p-12 text-center">
            <Loader2 className="mx-auto animate-spin text-[#C62828]" size={26}/>
          </div>
        )}

        {info && (
          <form onSubmit={submit} className="bg-white border border-[#EAE6DF] p-8 md:p-10" data-testid="activate-form">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C62828] font-semibold flex items-center gap-2">
              <ShieldCheck size={13}/> Secure Activation
            </p>
            <h1 className="font-heading text-3xl mt-3 text-[#0E2A5C]">Activate your account</h1>
            <p className="mt-3 text-sm text-[#4A4A4A] leading-relaxed">
              Welcome, <strong>{info.name}</strong>. Create a password for your
              <strong> {info.org_name}</strong> account (<span data-testid="activate-email">{info.email}</span>).
            </p>

            <div className="mt-8 grid gap-5">
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#4A4A4A]">New Password</label>
                <input type="password" value={password} required autoComplete="new-password"
                  onChange={(e) => setPassword(e.target.value)} data-testid="activate-password"
                  className="mt-1.5 w-full px-4 py-3.5 bg-white border border-[#EAE6DF] focus:border-[#0E2A5C] focus:outline-none text-sm" />
                <p className="mt-1 text-[11px] text-[#4A4A4A]">Minimum 8 characters.</p>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#4A4A4A]">Confirm Password</label>
                <input type="password" value={confirm} required autoComplete="new-password"
                  onChange={(e) => setConfirm(e.target.value)} data-testid="activate-confirm"
                  className="mt-1.5 w-full px-4 py-3.5 bg-white border border-[#EAE6DF] focus:border-[#0E2A5C] focus:outline-none text-sm" />
              </div>
            </div>

            {error && <p className="mt-4 text-sm text-[#C62828]" data-testid="activate-error">{error}</p>}

            <button type="submit" disabled={loading} data-testid="activate-submit"
              className="mt-7 w-full h-[52px] bg-[#C62828] hover:bg-[#B71C1C] disabled:opacity-60 text-white text-xs uppercase tracking-[0.2em] font-semibold flex items-center justify-center gap-2 transition-colors">
              {loading ? <Loader2 size={16} className="animate-spin"/> : <>Activate My Account <ArrowRight size={14}/></>}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
